import { useMutation } from '@apollo/client/react';
import { Alert, Box, Button, ButtonProps, Heading, Input, SimpleGrid, VStack, Field } from '@chakra-ui/react';
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers';
import { FormContainer } from '@src/components/sco/FormContainer';
import { CheckCircleIcon } from '@src/components/sco/icons';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { UpdateProfileInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
type UserBasicProfileFormFields = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};
type UserBasicProfileFormProps = GenericFormProps<UserBasicProfileFormFields> & {
  afterSuccess?: () => any;
  buttonProps?: ButtonProps;
  submitControlled?: boolean;
  submitTrigger?: number;
  title?: string;
};
export const UserBasicProfileForm = (props: UserBasicProfileFormProps) => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const [formErrorMessage, setFormErrorMessage] = useState<string>();
  const [formSuccessMessage, setFormSuccessMessage] = useState<string>();
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [updateProfile, updateProfileMutation] = useMutation(gql`
    mutation ($input: UpdateProfileInput!) {
      updateProfile(input: $input) {
        viewer {
          id
          currentUser {
            id
            email
            firstName
            lastName
            phone
          }
        }
      }
    }
  `);
  const form = useForm<UserBasicProfileFormFields>({
    defaultValues: {
      email: currentUser?.email,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      phone: currentUser?.phone
    }
  });
  const handleSubmit = useCallback(values => processSubmit({
    values,
    props,
    setFormErrorMessage,
    action: async values_0 => {
      const result = await updateProfile({
        variables: {
          input: {
            ...(values_0 as UpdateProfileInput)
          }
        }
      });
      return result;
    },
    afterSuccess: async (values_1, result_0) => {
      setFormSuccessMessage('Profile Saved');
      await refetchCurrentUser();
      if (props.afterSuccess) {
        await props.afterSuccess();
      }
    }
  }), []);
  useEffect(() => {
    if (props.submitTrigger) {
      form.handleSubmit(handleSubmit)();
    }
  }, [props.submitTrigger]);
  return <FormContainer {...props.containerProps}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <VStack align={'stretch'} gap={4} justify={'stretch'}>
          {props.title && <Heading size={'md'}>{props.title}</Heading>}

          {formErrorMessage && <Alert.Root borderRadius={'md'} status={'error'}>
              <Alert.Indicator />
              <Box marginLeft={'1em'}>{formErrorMessage}</Box>
            </Alert.Root>}

          <SimpleGrid columns={2} gap={4}>
            <Field.Root invalid={!!form.formState.errors.firstName}>
              <Field.Label>First Name</Field.Label>
              <Input type={'text'} {...form.register('firstName', {
              required: 'Required'
            })} />
              <Field.ErrorText>{form.formState.errors.firstName?.message}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!form.formState.errors.lastName}>
              <Field.Label>Last Name</Field.Label>
              <Input type={'text'} {...form.register('lastName', {
              required: 'Required'
            })} />
              <Field.ErrorText>{form.formState.errors.lastName?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>

          <Field.Root invalid={!!form.formState.errors.email}>
            <Field.Label>Email</Field.Label>
            <Input type={'text'} {...form.register('email', {
            required: 'Required'
          })} />
            <Field.ErrorText>{form.formState.errors.email?.message}</Field.ErrorText>
          </Field.Root>

          <Field.Root invalid={!!form.formState.errors.phone}>
            <Field.Label>
              {'Phone Number - '}
              <small>Include country code if outside U.S.</small>
            </Field.Label>
            <Input placeholder={'555-555-5555'} type={'tel'} {...form.register('phone', {})} />
            <Field.ErrorText>{form.formState.errors.phone?.message}</Field.ErrorText>
          </Field.Root>

          <div />
          <Button display={props.submitControlled ? 'none' : 'flex'} loading={form.formState.isSubmitting} textAlign={'center'} type={'submit'} variant={'cta'} width={'full'} {...props.buttonProps}>
            {formSuccessMessage ? <>
                <CheckCircleIcon marginRight={'1em'} /> {formSuccessMessage}
              </> : props.buttonProps?.title || 'Save Profile'}
          </Button>
        </VStack>
      </form>
    </FormContainer>;
};